import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, first, Subscription } from 'rxjs';
import { indicate } from 'src/app/operators';
import {
  ComponentGroupViewModel,
  ComponentPropertyViewModel,
  EsraPipingAPIClient,
} from 'src/app/shared/models/autogenerated-piping';
import {
  deserializeMaterialAstm,
  MATERIAL_ASTM_PROPERTY_NAME,
  MaterialAstmValue,
  serializeMaterialAstm,
} from 'src/app/shared/models/material-astm.model';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';

export type ComponentPropertyDictionaryType = 'ComponentProperty' | 'Valve' | '';

export interface ComponentPropertyEditorData {
  dictionaryType: ComponentPropertyDictionaryType;
  componentPropertyId: string | null;
}

@Component({
    selector: 'app-component-property-add-edit',
    templateUrl: './component-property-add-edit.component.html',
    styleUrl: './component-property-add-edit.component.scss',
    standalone: false
})
export class ComponentPropertyAddEditComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('editorContent') editorContent?: ElementRef<HTMLElement>;

  public dictionaryType: ComponentPropertyDictionaryType = '';
  public title: string = '';

  public componentGroupsArray: ComponentGroupViewModel[] = [];
  public componentGroupNameById: Map<string, string> = new Map<string, string>();

  public isEditMode = false;
  public componentPropertyNames: [string, string][] = [];

  addFormGroup: UntypedFormGroup;

  searching$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  saving$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isScrolledFromTop: boolean = false;
  isScrolledToBottom: boolean = false;

  private readonly subscriptions: Subscription[] = new Array<Subscription>();

  constructor(
    private readonly dialogRef: MatDialogRef<ComponentPropertyAddEditComponent, boolean>,
    private readonly _esraPipingAPIClient: EsraPipingAPIClient,
    private readonly _formBuilder: UntypedFormBuilder,
    private readonly _snackBarService: SnackBarService,
    @Inject(MAT_DIALOG_DATA) private readonly editorData: ComponentPropertyEditorData
  ) { }

  ngOnInit(): void {
    this.dictionaryType = this.editorData.dictionaryType;

    this.initFormGroup();

    this.loadComponentGroups();

    if (this.editorData.componentPropertyId) {
      this.loadExistingComponentProperty(this.editorData.componentPropertyId);
    } else {
      this.loadModelData();
    }
  }

  ngAfterViewInit(): void {
    requestAnimationFrame(() => {
      const editorContent: HTMLElement | undefined = this.editorContent?.nativeElement;
      if (editorContent) {
        this.updateScrollState(editorContent);
      }
    });
  }

  ngOnDestroy(): void {
    this.searching$.complete();
    this.saving$.complete();
    this.subscriptions.forEach(s => {
      if (s && !s.closed) {
        s.unsubscribe();
      }
    });
  }

  private loadComponentGroups(): void {
    this._esraPipingAPIClient
      .componentGroupsWithId()
      .pipe(
        indicate(this.searching$),
        first()
      )
      .subscribe({
        next: (groups: ComponentGroupViewModel[]) => {
          if (this.dictionaryType === "ComponentProperty") {
            this.componentGroupsArray = groups.filter(group => group.name !== 'Valves');
          }
          if (this.dictionaryType === "Valve") {
            this.componentGroupsArray = groups.filter(group => group.name === 'Valves');
          }

          this.componentGroupNameById = new Map<string, string>(
            this.componentGroupsArray
              .filter((group: ComponentGroupViewModel) => !!group?.id && !!group?.name)
              .map((group: ComponentGroupViewModel) => [group.id as string, group.name as string])
          );

          if (this.componentGroupsArray.length === 1) {
            this.addFormGroup.patchValue({
              componentGroupId: this.componentGroupsArray[0].id
            });
          }

          this.loadAttributeNamesForGroup(this.addFormGroup.get('componentGroupId')?.value);
        }
      });
  }

  private loadExistingComponentProperty(componentPropertyId: string): void {
    this._esraPipingAPIClient
      .getComponentProperty(componentPropertyId)
      .pipe(
        indicate(this.searching$),
        first()
      )
      .subscribe({
        next: (componentProperty: ComponentPropertyViewModel) => {
          this.loadModelData(componentProperty);
        },
        error: () => {
          this._snackBarService.showError('Unable to load the selected property.');
          this.dialogRef.close(false);
        }
      });
  }

  private loadModelData(searchResponse?: ComponentPropertyViewModel): void {
    if (searchResponse) {
      this.addFormGroup.patchValue(searchResponse);
      this.isEditMode = true;

      if (this.isMaterialAstmProperty) {
        const materialAstm: MaterialAstmValue = deserializeMaterialAstm(searchResponse.value);
        this.addFormGroup.patchValue({ material: materialAstm.material, astm: materialAstm.astm });
      }

      this.loadAttributeNamesForGroup(this.addFormGroup.get('componentGroupId')?.value);
    } else {
      let defaultViewModel: ComponentPropertyViewModel = new ComponentPropertyViewModel();
      defaultViewModel.isActive = true;
      this.addFormGroup.patchValue(defaultViewModel);
    }

    this.setTitle(this.isEditMode);
  }

  private loadAttributeNamesForGroup(componentGroupId: string | null | undefined): void {
    const groupId: string = componentGroupId ?? '';
    const groupName: string = this.componentGroupNameById.get(groupId) ?? '';

    if (!groupName) {
      this.componentPropertyNames = [];
      this.addFormGroup.get('name')?.setValue('');
      return;
    }

    this._esraPipingAPIClient
      .getAttributeNamesByGroup(groupName)
      .pipe(
        indicate(this.searching$),
        first()
      )
      .subscribe({
        next: (attributeNames: string[]) => {
          const currentName: string = this.addFormGroup.get('name')?.value ?? '';
          const canonicalNames: string[] = (attributeNames ?? []).filter((attributeName: string) => !!attributeName);

          this.componentPropertyNames = canonicalNames.map((attributeName: string) => [attributeName, attributeName]);

          if (!!currentName && !canonicalNames.includes(currentName)) {
            this.componentPropertyNames = [[currentName, currentName], ...this.componentPropertyNames];
          }
        },
        error: () => {
          this.componentPropertyNames = [];
          this._snackBarService.showError('Unable to load property names for the selected group.');
        },
      });
  }

  private initFormGroup() {
    this.addFormGroup = this._formBuilder.group({
      id: ['00000000-0000-0000-0000-000000000000'],
      componentGroupId: ['', Validators.required],
      name: ['', Validators.required],
      description: [''],
      value: ['', Validators.required],
      material: [''],
      astm: [''],
      code: [''],
      isActive: [false],
    });

    this.subscriptions.push(
      this.addFormGroup.get('name').valueChanges.subscribe(() => this.applyMaterialAstmValidators())
    );

    this.subscriptions.push(
      this.addFormGroup.get('componentGroupId').valueChanges.subscribe((componentGroupId: string) => {
        this.loadAttributeNamesForGroup(componentGroupId);
        if (!this.isEditMode) {
          this.addFormGroup.get('name')?.setValue('');
        }
      })
    );
  }

  /**
   * True when the property being edited is the combined Material/ASTM property, whose stored
   * value is JSON rather than plain text. Drives the split two-input layout in the template.
   */
  get isMaterialAstmProperty(): boolean {
    return this.addFormGroup?.get('name')?.value === MATERIAL_ASTM_PROPERTY_NAME;
  }

  /**
   * Swaps the required-field validation between the single `value` input and the split
   * Material/ASTM inputs. Material and ASTM are each optional on their own -- the reference data
   * contains rows with only one side populated -- so they are validated together in `save()`.
   */
  private applyMaterialAstmValidators(): void {
    const isMaterialAstm: boolean = this.isMaterialAstmProperty;

    this.addFormGroup.get('value').setValidators(isMaterialAstm ? [] : [Validators.required]);
    this.addFormGroup.get('value').updateValueAndValidity({ emitEvent: false });
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  handleEditorScroll(event: Event): void {
    const editorContent: HTMLElement = event.target as HTMLElement;
    this.updateScrollState(editorContent);
  }

  private updateScrollState(editorContent: HTMLElement): void {
    const remainingScroll: number = editorContent.scrollHeight - editorContent.scrollTop - editorContent.clientHeight;
    this.isScrolledFromTop = editorContent.scrollTop > 8;
    this.isScrolledToBottom = remainingScroll <= 24;
  }

  private findInvalidControls(untypedFormGroup: UntypedFormGroup): string[] {
    const invalid: string[] = [];
    const controls = untypedFormGroup.controls;
    for (const name in controls) {
      if (controls[name].invalid) {
        invalid.push(name);
      }
    }
    return invalid;
  }

  save(): void {
    if (!this.addFormGroup.valid) {
      this.addFormGroup.markAllAsTouched();
      const invalidFields = this.findInvalidControls(this.addFormGroup);
      const errorMessage = 'Please correct errors in the selected fields: '
        + invalidFields;
      this._snackBarService.showSnackBar(
        true,
        errorMessage
      );
      return;
    }

    const data: ComponentPropertyViewModel = this.addFormGroup.getRawValue();

    if (this.isMaterialAstmProperty) {
      const material: string = this.addFormGroup.get('material').value?.trim() ?? '';
      const astm: string = this.addFormGroup.get('astm').value?.trim() ?? '';

      if (!material && !astm) {
        this._snackBarService.showSnackBar(true, 'Please provide a Material, an ASTM, or both.');
        return;
      }

      data.value = serializeMaterialAstm(material, astm);
    }

    let saveSubscription: Subscription;
    if (this.isEditMode) {
      saveSubscription = this._esraPipingAPIClient
        .updateComponentProperty(data)
        .pipe(indicate(this.saving$), first())
        .subscribe({
          next: (_) => {
            this.onSaveSuccess();
          },
          error: (error) => {
            this.onSaveError(error);
          }
        });
    } else {
      saveSubscription = this._esraPipingAPIClient
        .createComponentProperty(data)
        .pipe(indicate(this.saving$), first())
        .subscribe({
          next: (_) => {
            this.onSaveSuccess()
          },
          error: (error) => {
            this.onSaveError(error);
          }
        });
    }

    this.subscriptions.push(saveSubscription);
  }

  private onSaveSuccess(): void {
    const name = this.addFormGroup.controls['name'].value;
    const value = this.isMaterialAstmProperty
      ? `${this.addFormGroup.controls['material'].value}, ${this.addFormGroup.controls['astm'].value}`
      : this.addFormGroup.controls['value'].value;
    let successMessage: string;
    if (this.isEditMode) {
      successMessage = `Your ${this.dictionaryType === "Valve" ? "valve" : "component"} property ${name}, ${value} was succesfully edited`;
    } else {
      successMessage = `Your ${this.dictionaryType === "Valve" ? "valve" : "component"} property ${name}, ${value} was succesfully created`;
    }
    this._snackBarService.showSnackBar(
      true,
      successMessage
    );
    this.dialogRef.close(true);
  }

  private onSaveError(error: unknown): void {
    const backendMessage: string = this.getBackendErrorMessage(error);
    this._snackBarService.showError(backendMessage);
  }

  private getBackendErrorMessage(error: unknown): string {
    if (!error || typeof error !== 'object') {
      return 'Unable to save the component property.';
    }

    const errorWithPayload: Record<string, unknown> = error as Record<string, unknown>;
    const responseText: string = typeof errorWithPayload.response === 'string' ? errorWithPayload.response : '';
    if (responseText) {
      return responseText;
    }

    const messageText: string = typeof errorWithPayload.message === 'string' ? errorWithPayload.message : '';
    if (messageText) {
      return messageText;
    }

    return 'Unable to save the component property.';
  }

  private setTitle(isEdit: boolean): void {
    if (isEdit) {
      this.title = 'Edit ';
    } else {
      this.title = 'Create new ';
    }

    switch (this.dictionaryType) {
      case "ComponentProperty":
        this.title += "component property";
        break;
      case "Valve":
        this.title += "valve property";
        break;
      default:
        this.title = "";
        break;
    }
  }

}
